import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Dimensions, View, Text, Pressable, Platform, Animated } from 'react-native';
import Svg, { Path, Circle, G, Defs, LinearGradient, Stop, Marker } from 'react-native-svg';
import { AvatarGroup, Avatar } from '@mui/material';
import { AntDesign } from '@expo/vector-icons';
import { interpolateColor } from 'react-native-reanimated';
import { Popover, SizableText } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

const AnimatedPath = Animated.createAnimatedComponent(Path);

const window = Dimensions.get('window');
const screenWidth = window.width;
const screenHeight = window.height;

const Point = ({ x, y }) => ({ x: +x, y: +y });

// Helper function to determine connection direction based on relative positions
const getConnectionDirection = (point: { x: number; y: number }, targetPoint: { x: number; y: number }) => {
  const deltaX = targetPoint.x - point.x;
  const deltaY = targetPoint.y - point.y;

  // Determine primary direction based on larger delta
  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    return deltaX > 0 ? 'right' : 'left';
  } else {
    return deltaY > 0 ? 'bottom' : 'top';
  }
};

// Helper function to calculate control points based on connection direction
const getControlPoint = (point: { x: number; y: number }, direction: string, distance: number, perpendicularOffset: number) => {
  switch (direction) {
    case 'right':
      return { x: point.x + distance, y: point.y }; // No vertical offset for horizontal connections
    case 'left':
      return { x: point.x - distance, y: point.y }; // No vertical offset for horizontal connections
    case 'bottom':
      return { x: point.x + perpendicularOffset, y: point.y + distance }; // Add perpendicular offset for visibility
    case 'top':
      return { x: point.x + perpendicularOffset, y: point.y - distance }; // Add perpendicular offset for visibility
    default:
      return { x: point.x + distance, y: point.y };
  }
};

const ArrowView = ({ gap = 40, startColor = '#5ac0ea',
  endColor = '#a9c0ff', pressCallback, data, showAvs, from, text, to, position = "bottom", curveDelta = 120, dash = [0, 0], cap = 'round', color = '#000', width = 2, debug = false, animate = true, showPlusCircle = true }) => {

  const [endPoint, setEndPoint] = useState(to);
  const [showAvsval, setShowAvs] = useState(2);
  const zero = Point({ x: 0, y: 0 });
  const fromPoint = from ? Point(from) : zero;
  const toPoint = to ? Point(to) : zero;
  const theme = themeSelector();
  const popRefs = useRef([]);

  // Generate unique gradient ID to prevent conflicts between multiple arrow instances
  const gradientId = useRef(`grad-${Math.random().toString(36).substr(2, 9)}`).current;

  const midPoint = {
    x: (fromPoint.x + toPoint.x) / 2,
    y: (fromPoint.y + toPoint.y) / 2,
  };

  // Professional flow handler approach: straight segments + smooth curves
  const deltaX = toPoint.x - fromPoint.x;
  const deltaY = toPoint.y - fromPoint.y;
  const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

  // Professional flow design: smooth curves that complement connection points
  const isTargetToRight = toPoint.x > fromPoint.x;
  const isTargetBelow = toPoint.y > fromPoint.y;

  // Calculate connection angles for natural flow
  const angle = Math.atan2(toPoint.y - fromPoint.y, toPoint.x - fromPoint.x);

  // Determine connection directions for proper curve shaping
  const fromDirection = getConnectionDirection(from, toPoint);
  const toDirection = getConnectionDirection(to, fromPoint);

  // Calculate control points based on connection directions and distance
  const isHorizontalConnection = Math.abs(deltaX) > Math.abs(deltaY);
  const isCompletelyHorizontal = Math.abs(deltaY) < 2; // Detect horizontal lines (allow small Y difference for rounding)
  const hasSignificantVerticalDifference = Math.abs(deltaY) > 100; // Significant vertical offset

  let controlPoint1, controlPoint2, straightStart, straightEnd;

  if (isCompletelyHorizontal) {
    // For completely horizontal lines, use a guaranteed visible straight line
    controlPoint1 = fromPoint; // Start point
    controlPoint2 = toPoint; // End point (creates straight line)
  } else if (isHorizontalConnection) {
    // For mostly horizontal connections, use a straight line with no curves
    controlPoint1 = fromPoint; // Start point
    controlPoint2 = toPoint; // End point (creates straight line)
  } else if (hasSignificantVerticalDifference) {
    // For connections with significant vertical differences, add horizontal buffer segments
    const bufferLength = 30; // Horizontal buffer for polished look
    const controlDistance = Math.min(distance * 0.3, 80);

    // Always extend horizontally from connection points for polished appearance
    straightStart = {
      x: fromPoint.x + (fromDirection === 'right' ? bufferLength : -bufferLength),
      y: fromPoint.y
    };
    straightEnd = {
      x: toPoint.x + (toDirection === 'right' ? -bufferLength : bufferLength),
      y: toPoint.y
    };

    const perpendicularOffset = 15; // Small perpendicular offset for smooth curves
    controlPoint1 = getControlPoint(straightStart, 'right', controlDistance, perpendicularOffset);
    controlPoint2 = getControlPoint(straightEnd, 'left', controlDistance, perpendicularOffset);
  } else {
    // For standard vertical connections, use normal curve calculation
    const controlDistance = Math.min(distance * 0.4, 120);
    const perpendicularOffset = Math.min(distance * 0.2, 60);
    controlPoint1 = getControlPoint(fromPoint, fromDirection, controlDistance, perpendicularOffset);
    controlPoint2 = getControlPoint(toPoint, toDirection, controlDistance, perpendicularOffset);
  }

  const pulseAnim = useRef(new Animated.Value(1)).current;
  const colorAnim = useRef(new Animated.Value(0)).current;

  // Calculate mid-color as a blend between start and end for better gradient
  const calculateMidColor = (start: string, end: string) => {
    // For simple hex colors, just use the start color
    // This could be enhanced with actual color blending if needed
    return start;
  };

  const [gradientColors, setGradientColors] = useState({
    start: startColor,
    mid: calculateMidColor(startColor, endColor),
    end: endColor,
  });

  const startPulsing = () => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.005,
          duration: 500,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
      ])
    ).start();
  };

  const startColorAnimation = () => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(colorAnim, {
          toValue: 1,
          duration: 2000,
          useNativeDriver: false,
        }),
        Animated.timing(colorAnim, {
          toValue: 0,
          duration: 2000,
          useNativeDriver: false,
        }),
      ])
    ).start();

    colorAnim.addListener(({ value }) => {
      setGradientColors({
        start: interpolateColor(value, [0, 1], [startColor, endColor]),
        mid: interpolateColor(value, [0, 1], [startColor, endColor]),
        end: interpolateColor(value, [0, 1], [endColor, startColor]),
      });
    });
  };

  useEffect(() => {
    if (animate) {
      startPulsing();
      startColorAnimation();
    } else {
      // When not animating, set gradient colors directly from props
      const newColors = {
        start: startColor,
        mid: calculateMidColor(startColor, endColor),
        end: endColor,
      };
      setGradientColors(newColors);

      if (debug) {
        console.log('🎨 Setting gradient colors (no animation):', newColors);
      }
    }
    return () => {
      colorAnim.removeAllListeners();
    };
  }, [animate, startColor, endColor, debug]);

  // Calculate dynamic SVG dimensions based on arrow bounding box to prevent Android bitmap size issues
  // Using useMemo to avoid recalculating on every render
  const svgDimensions = useMemo(() => {
    // Get all points that will be in the path
    const points = [fromPoint, toPoint];
    if (straightStart) points.push(straightStart);
    if (straightEnd) points.push(straightEnd);
    if (controlPoint1) points.push(controlPoint1);
    if (controlPoint2) points.push(controlPoint2);

    // Find bounding box
    const minX = Math.min(...points.map(p => p.x));
    const maxX = Math.max(...points.map(p => p.x));
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));

    // Handle edge case where all points are the same
    const width = maxX - minX;
    const height = maxY - minY;

    // Add padding for arrow markers and stroke width
    const padding = 100;
    const svgWidth = Math.max(width + padding * 2, 200); // Minimum 200px
    const svgHeight = Math.max(height + padding * 2, 200); // Minimum 200px

    // For Android, use viewBox approach to avoid creating large bitmaps
    // Calculate the actual bounding box dimensions
    const actualWidth = width + padding * 2;
    const actualHeight = height + padding * 2;

    if (Platform.OS === 'android') {
      // CRITICAL: On Android, react-native-svg creates bitmaps based on viewBox size
      // We MUST keep viewBox dimensions small (800x800 max) to prevent crashes
      // If arrow is longer than 800px, it will be clipped but that's better than crashing
      const maxDimension = 800; // Maximum viewBox and canvas size for Android
      const constrainedViewBoxWidth = Math.min(actualWidth, maxDimension);
      const constrainedViewBoxHeight = Math.min(actualHeight, maxDimension);

      // Use fixed small canvas and viewBox sizes - this prevents Android from creating huge bitmaps
      return {
        width: maxDimension, // Fixed small canvas size
        height: maxDimension,
        offsetX: minX - padding,
        offsetY: minY - padding,
        viewBox: `0 0 ${constrainedViewBoxWidth} ${constrainedViewBoxHeight}`
      };
    }

    // For other platforms, use explicit dimensions
    return {
      width: Math.min(actualWidth, 4000),
      height: Math.min(actualHeight, 4000),
      offsetX: minX - padding,
      offsetY: minY - padding,
      viewBox: undefined
    };
  }, [fromPoint, toPoint, straightStart, straightEnd, controlPoint1, controlPoint2]);

  return (
    <View style={{ zIndex: 0 }}>
      <View
        style={{
          zIndex: 0,
          borderColor: 'red',
          borderWidth: debug ? 1 : 0,
          position: 'absolute',
          overflow: "visible",
        }}>
        <View style={{ position: 'absolute', overflow: 'visible', left: svgDimensions.offsetX, top: svgDimensions.offsetY }}>
          <Svg
            width={svgDimensions.width}
            height={svgDimensions.height}
            viewBox={svgDimensions.viewBox}
            style={{ overflow: 'visible' }}
          >
            <Defs>
              <LinearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                <Stop offset="0%" stopColor={gradientColors.start} />
                <Stop offset="50%" stopColor={gradientColors.mid} />
                <Stop offset="100%" stopColor={gradientColors.end} />
              </LinearGradient>
              <Marker
                id={`${gradientId}-semiCircle`}
                viewBox="0 0 10 10"
                refX="10" refY="5"
                orient="auto"
                markerWidth="7"
                markerHeight="7"
              >
                <Path d="M0,5 A5,5 0 0,1 10,5" stroke="rgba(0, 0, 0, 0.3)" strokeWidth={(width / 2) + 1} transform="translate(1, 1)" fill="transparent" />
                <Path d="M0,5 A5,5 0 0,1 10,5" stroke={`url(#${gradientId})`} fill="transparent" strokeWidth={width / 2} />
              </Marker>
              <Marker
                id={`${gradientId}-semiCircleStart`}
                viewBox="0 0 10 10"
                refX="0" refY="5"
                orient="auto"
                markerWidth="7"
                markerHeight="7"
              >
                <Path strokeWidth={width / 2} d="M0,5 A5,5 0 0,0 10,5" stroke={`url(#${gradientId})`} fill="transparent" />
              </Marker>
            </Defs>
            {/* Helper function to normalize coordinates for Android viewBox */}
            {(() => {
              const normalizeCoord = (coord: number, offset: number) => {
                if (Platform.OS === 'android' && svgDimensions.viewBox) {
                  // For Android with viewBox, coordinates are relative to viewBox origin
                  return coord - offset;
                }
                return coord - offset;
              };

              const normFromX = normalizeCoord(fromPoint.x, svgDimensions.offsetX);
              const normFromY = normalizeCoord(fromPoint.y, svgDimensions.offsetY);
              const normToX = normalizeCoord(toPoint.x, svgDimensions.offsetX);
              const normToY = normalizeCoord(toPoint.y, svgDimensions.offsetY);
              const normStraightStartX = straightStart ? normalizeCoord(straightStart.x, svgDimensions.offsetX) : 0;
              const normStraightStartY = straightStart ? normalizeCoord(straightStart.y, svgDimensions.offsetY) : 0;
              const normStraightEndX = straightEnd ? normalizeCoord(straightEnd.x, svgDimensions.offsetX) : 0;
              const normStraightEndY = straightEnd ? normalizeCoord(straightEnd.y, svgDimensions.offsetY) : 0;
              const normControl1X = controlPoint1 ? normalizeCoord(controlPoint1.x, svgDimensions.offsetX) : 0;
              const normControl1Y = controlPoint1 ? normalizeCoord(controlPoint1.y, svgDimensions.offsetY) : 0;
              const normControl2X = controlPoint2 ? normalizeCoord(controlPoint2.x, svgDimensions.offsetX) : 0;
              const normControl2Y = controlPoint2 ? normalizeCoord(controlPoint2.y, svgDimensions.offsetY) : 0;

              return (
                <>
                  {/* Shadow/outline for horizontal lines for better visibility */}
                  {isCompletelyHorizontal && (
                    <AnimatedPath
                      d={`M ${normFromX},${normFromY} L ${normToX},${normToY}`}
                      strokeWidth={width * 2.5 + 2}
                      stroke="rgba(0, 0, 0, 0.3)"
                      fill="transparent"
                      strokeLinecap="round"
                    />
                  )}

                  <AnimatedPath
                    d={isCompletelyHorizontal
                      ? `M ${normFromX},${normFromY} L ${normToX},${normToY}`
                      : isHorizontalConnection
                        ? `M ${normFromX},${normFromY} L ${normToX},${normToY}`
                        : hasSignificantVerticalDifference
                          ? `M ${normFromX},${normFromY} L ${normStraightStartX},${normStraightStartY} C ${normControl1X},${normControl1Y} ${normControl2X},${normControl2Y} ${normStraightEndX},${normStraightEndY} L ${normToX},${normToY}`
                          : `M ${normFromX},${normFromY} C ${normControl1X},${normControl1Y} ${normControl2X},${normControl2Y} ${normToX},${normToY}`
                    }
                    strokeWidth={isCompletelyHorizontal ? width * 2.5 : width}
                    stroke={`url(#${gradientId})`}
                    strokeDasharray={dash}
                    fill="transparent"
                    strokeLinecap="round"
                    {...(animate && { transform: [{ scale: pulseAnim }] })}
                  />
                </>
              );
            })()}
          </Svg>

          {showPlusCircle && (
            <Popover placement="right" ref={el => { if (el) popRefs.current.push(el); }} key={`${from.id}-${to.id}`} stayInFrame allowFlip size={"$4"}>
              <Popover.Trigger
                asChild
                style={{
                  position: 'absolute',
                  left: midPoint.x - svgDimensions.offsetX - 20,
                  top: midPoint.y - svgDimensions.offsetY - 20,
                  zIndex: 0,
                  width: 40,
                  height: 40,
                  backgroundColor: 'transparent',
                }}>
                <Pressable hitSlop={20}>
                  <AntDesign name="plus-circle" color="rgb(90,192,234)" size={48} />
                </Pressable>
              </Popover.Trigger>
              <Popover.Content enterStyle={{ y: 10, opacity: 0 }} exitStyle={{ y: 10, opacity: 0 }} elevate shadowRadius={20} shadowOpacity={.2} overflow='visible' style={{ shadowRadius: 20, shadowOpacity: .2, top: 3, left: 10, backgroundColor: theme ? "white" : "#f3f3f3" }}>
                <Popover.Arrow borderWidth={10} borderColor="rgb(90,192,234)" />
                <View style={{ borderRadius: 20, zIndex: 0, flex: 1 }}>
                  <SizableText size={2} style={{ padding: 10, color: theme ? "black" : "black" }}>Link: {text}</SizableText>
                </View>
              </Popover.Content>
            </Popover>
          )}
        </View>
      </View>
      {showAvs && (
        <Pressable onPress={() => pressCallback(data.code)} id={"avsLink" + (from.id)} onHoverIn={() => setShowAvs(5)} onHoverOut={() => setShowAvs(2)} style={{ position: 'absolute', left: toPoint.x === 0 ? 50 : toPoint.x, top: toPoint.y, zIndex: 0 }}>
          <AvatarGroup style={{ height: 40 }} spacing={'small'} max={showAvsval}>
            {data?.users?.map((user) => <Avatar key={user?.id} src={user?.user?.profile_information?.avatar?.img} />)}
          </AvatarGroup>
        </Pressable>
      )}
    </View>
  );
};

export default ArrowView;
